import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  ButtonBase,
  Typography,
  Box,
  Alert,
  Chip,
  Grid,
  TextField,
  Stack,
  CircularProgress,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { transactionService } from 'src/services/transaction.service';
import { savingsAccountService } from 'src/services/savingsAccount.service';
import { Tables } from 'src/types/db.types';
import { ToastNotifications } from 'src/components';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';
import { chargeIcon, chargeNote, chargeTitle } from 'src/utils/chargeDisplay';

interface TransactionActionModalProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  studentId: string;
  currentBalance: number;
  /** Where the popup opens: the three choices, or straight to "pay from". */
  initialStep?: ModalStep;
}

type Transaction = Tables<'transactions'> & {
  transaction_type: Tables<'transaction_types'>;
  student?: {
    id: string;
    user: { firstname: string; lastname: string };
  };
};

type ActionType = 'pay' | 'defer' | 'question';
export type ModalStep = 'action-selection' | 'payment-source' | 'deferral-options' | 'question-form' | 'confirmation';

/** A big tappable tile. selected draws the ring; tone tints the icon. */
function Tile({
  icon,
  tone,
  label,
  caption,
  selected,
  disabled,
  onClick,
  trailing,
}: {
  icon: string;
  tone: string;
  label: string;
  caption?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  trailing?: React.ReactNode;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      sx={{
        width: '100%',
        height: '100%',
        p: 1.75,
        borderRadius: 2,
        textAlign: 'left',
        justifyContent: 'flex-start',
        gap: 1.5,
        border: '2px solid',
        borderColor: selected ? tone : 'divider',
        bgcolor: selected ? alpha(tone, 0.06) : 'background.paper',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: disabled ? 'divider' : tone },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          flexShrink: 0,
          borderRadius: 1.75,
          bgcolor: alpha(tone, 0.12),
          color: tone,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon icon={icon} width={24} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          {label}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
            {caption}
          </Typography>
        )}
      </Box>
      {trailing}
    </ButtonBase>
  );
}

export default function TransactionActionModal({
  open,
  onClose,
  transaction,
  studentId,
  currentBalance,
  initialStep = 'action-selection',
}: TransactionActionModalProps) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);
  const [currentStep, setCurrentStep] = useState<ModalStep>('action-selection');
  const [paymentSource, setPaymentSource] = useState<'balance' | 'savings'>('balance');
  const [selectedSavingsAccount, setSelectedSavingsAccount] = useState<string>('');
  const [deferralPeriod, setDeferralPeriod] = useState<string>('3');
  const [customDueDate, setCustomDueDate] = useState<string>('');
  const [questionText, setQuestionText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch savings accounts
  const { data: savingsAccounts = [] } = useQuery({
    queryKey: ['savings-accounts', studentId],
    queryFn: () => savingsAccountService.getStudentSavingsAccounts(studentId),
    enabled: !!studentId && open,
  });

  // Reset state when modal opens/closes or transaction changes
  useEffect(() => {
    if (open && transaction) {
      setSelectedAction(initialStep === 'payment-source' ? 'pay' : null);
      setCurrentStep(initialStep);
      setPaymentSource('balance');
      setSelectedSavingsAccount('');
      setDeferralPeriod('3');
      setCustomDueDate('');
      setQuestionText('');
      setValidationError('');
    }
  }, [open, transaction, initialStep]);

  // Mutations for actions
  const payTransactionMutation = useMutation({
    mutationFn: ({
      transactionId,
      action,
      savingsAccountId,
    }: {
      transactionId: string;
      action: 'approve' | 'deny';
      savingsAccountId?: string;
    }) =>
      transactionService.respondToTransaction(transactionId, studentId, action, {
        payment_source: action === 'approve' ? paymentSource : undefined,
        savings_account_id: savingsAccountId,
      }),
    onSuccess: async () => {
      await invalidateStudentFinancialQueries(queryClient);
      ToastNotifications.success('Payment processed successfully!');
      onClose();
    },
    onError: (error: any) => {
      ToastNotifications.error(error.message || 'Failed to process payment');
    },
  });

  const deferTransactionMutation = useMutation({
    mutationFn: ({ transactionId, dueDate, reason }: { transactionId: string; dueDate: Date; reason: string }) =>
      transactionService.deferTransaction(transactionId, studentId, dueDate, reason),
    onSuccess: async () => {
      await invalidateStudentFinancialQueries(queryClient);
      ToastNotifications.success('Payment deferred successfully!');
      onClose();
    },
    onError: (error: any) => {
      ToastNotifications.error(error.message || 'Failed to defer payment');
    },
  });

  const questionTransactionMutation = useMutation({
    mutationFn: ({ transactionId, questionText }: { transactionId: string; questionText: string }) =>
      transactionService.questionTransaction(transactionId, studentId, questionText),
    onSuccess: async () => {
      await invalidateStudentFinancialQueries(queryClient);
      ToastNotifications.success('Question submitted successfully!');
      onClose();
    },
    onError: (error: any) => {
      ToastNotifications.error(error.message || 'Failed to submit question');
    },
  });

  const handlePayNowClick = () => {
    setSelectedAction('pay');
    setCurrentStep('payment-source');
  };

  const handleDeferPaymentClick = () => {
    if (transaction && (transaction.deferred_count || 0) >= 3) {
      setValidationError('You have used all 3 requests for more time.');
      return;
    }
    setSelectedAction('defer');
    setCurrentStep('deferral-options');
  };

  const handleQuestionClick = () => {
    setSelectedAction('question');
    setCurrentStep('question-form');
  };

  const handlePaymentSourceContinue = () => {
    if (paymentSource === 'balance' && currentBalance < (transaction?.amount || 0)) {
      setValidationError(
        `Not enough in your main balance. You need $${((transaction?.amount || 0) - currentBalance).toFixed(2)} more.`
      );
      return;
    }
    if (paymentSource === 'savings' && !selectedSavingsAccount) {
      setValidationError('Pick where the money comes from.');
      return;
    }
    const selectedAccount = savingsAccounts.find((acc) => acc.id === selectedSavingsAccount);
    if (paymentSource === 'savings' && selectedAccount && selectedAccount.current_amount < (transaction?.amount || 0)) {
      setValidationError('Not enough in that savings account. Pick another one.');
      return;
    }
    setValidationError('');
    setCurrentStep('confirmation');
  };

  const handleConfirmPayment = async () => {
    if (!transaction) return;

    setIsProcessing(true);
    try {
      await payTransactionMutation.mutateAsync({
        transactionId: transaction.id,
        action: 'approve',
        savingsAccountId: paymentSource === 'savings' ? selectedSavingsAccount : undefined,
      });
    } catch {
      // onError above already surfaces this via a toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDeferral = async () => {
    if (!transaction) return;

    setIsProcessing(true);
    try {
      let dueDate: Date;
      let reason: string;

      if (deferralPeriod === 'custom') {
        if (!customDueDate) {
          setValidationError('Pick a date first.');
          setIsProcessing(false);
          return;
        }
        dueDate = new Date(customDueDate);
        reason = 'Deferred until custom date';
      } else {
        dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + parseInt(deferralPeriod));
        reason = `Deferred for ${deferralPeriod} days`;
      }

      await deferTransactionMutation.mutateAsync({
        transactionId: transaction.id,
        dueDate,
        reason,
      });
    } catch {
      // onError above already surfaces this via a toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuestionSubmit = async () => {
    if (!transaction || !questionText.trim()) {
      setValidationError('Type your question first.');
      return;
    }

    setIsProcessing(true);
    try {
      await questionTransactionMutation.mutateAsync({
        transactionId: transaction.id,
        questionText: questionText.trim(),
      });
    } catch {
      // onError above already surfaces this via a toast
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackClick = () => {
    if (currentStep === 'payment-source') {
      setCurrentStep('action-selection');
      setSelectedAction(null);
    } else if (currentStep === 'confirmation') {
      setCurrentStep('payment-source');
    } else if (currentStep === 'deferral-options') {
      setCurrentStep('action-selection');
      setSelectedAction(null);
    } else if (currentStep === 'question-form') {
      setCurrentStep('action-selection');
      setSelectedAction(null);
    }
    setValidationError('');
  };

  if (!transaction) return null;

  const MAX_DEFERRALS = 3;
  const amount = Math.abs(Number(transaction.amount || 0));
  const isCredit = transaction.transaction_type?.type === 'credit';
  const title = chargeTitle(transaction);
  const note = chargeNote(transaction);
  const deferralsLeft = Math.max(0, MAX_DEFERRALS - (transaction.deferred_count || 0));
  const maxDeferralsReached = deferralsLeft === 0;
  const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;
  const selectedAccount = savingsAccounts.find((acc) => acc.id === selectedSavingsAccount);
  const sourceAmount = paymentSource === 'balance' ? currentBalance : Number(selectedAccount?.current_amount || 0);
  const sourceName = paymentSource === 'balance' ? 'Main balance' : selectedAccount?.name || 'Savings';

  const STEP_TITLES: Record<ModalStep, string> = {
    'action-selection': 'What do you want to do?',
    'payment-source': 'Where should the money come from?',
    confirmation: 'Check and pay',
    'deferral-options': 'How much more time do you need?',
    'question-form': 'Ask about this charge',
  };

  const errorNote = () =>
    validationError ? (
      <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
        {validationError}
      </Alert>
    ) : null;

  const footer = (children?: React.ReactNode) => (
    <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 3 }}>
      {currentStep === 'action-selection' ? (
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Not now
        </Button>
      ) : (
        <Button
          onClick={handleBackClick}
          startIcon={<Icon icon="solar:arrow-left-linear" width={16} />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Back
        </Button>
      )}
      {children}
    </Stack>
  );

  const primarySx = {
    height: 42,
    px: 2.5,
    borderRadius: 1.75,
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
  } as const;

  const renderActionSelector = () => (
    <Box>
      <Grid container spacing={1.25}>
        <Grid item xs={12}>
          <Tile
            icon="solar:wallet-money-bold-duotone"
            tone="#059669"
            label="Pay now"
            caption={`Pay ${money(amount)} and you are done`}
            onClick={handlePayNowClick}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tile
            icon="solar:calendar-add-bold-duotone"
            tone="#D97706"
            label="Get more time"
            caption={
              maxDeferralsReached
                ? 'You have used all 3 requests'
                : `${deferralsLeft} of ${MAX_DEFERRALS} requests left`
            }
            disabled={maxDeferralsReached}
            onClick={handleDeferPaymentClick}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tile
            icon="solar:chat-round-dots-bold-duotone"
            tone="#2563EB"
            label="Ask a question"
            caption="Think this is wrong? Tell your teacher"
            onClick={handleQuestionClick}
          />
        </Grid>
      </Grid>
      {errorNote()}
      {footer()}
    </Box>
  );

  const renderPaymentSourceSelection = () => {
    const sources = [
      { id: 'balance', name: 'Main balance', balance: currentBalance, icon: 'solar:card-2-bold-duotone' },
      ...savingsAccounts.map((acc) => ({
        id: acc.id,
        name: acc.name,
        balance: Number(acc.current_amount || 0),
        icon: 'solar:safe-square-bold-duotone',
      })),
    ];
    return (
      <Box>
        <Stack spacing={1.25}>
          {sources.map((src) => {
            const isBalance = src.id === 'balance';
            const selected = isBalance
              ? paymentSource === 'balance'
              : paymentSource === 'savings' && selectedSavingsAccount === src.id;
            const enough = src.balance >= amount;
            return (
              <Tile
                key={src.id}
                icon={src.icon}
                tone={enough ? '#059669' : '#D97706'}
                label={`${src.name} (${money(src.balance)})`}
                caption={
                  enough
                    ? `${money(src.balance - amount)} left after paying`
                    : `You need ${money(amount - src.balance)} more`
                }
                selected={selected}
                onClick={() => {
                  setValidationError('');
                  if (isBalance) {
                    setPaymentSource('balance');
                    setSelectedSavingsAccount('');
                  } else {
                    setPaymentSource('savings');
                    setSelectedSavingsAccount(src.id);
                  }
                }}
                trailing={
                  <Chip
                    label={enough ? 'Enough' : 'Not enough'}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: '0.6875rem',
                      bgcolor: alpha(enough ? '#059669' : '#D97706', 0.12),
                      color: enough ? '#059669' : '#B45309',
                    }}
                  />
                }
              />
            );
          })}
        </Stack>
        {errorNote()}
        {footer(
          <Button variant="contained" onClick={handlePaymentSourceContinue} sx={primarySx}>
            Continue
          </Button>
        )}
      </Box>
    );
  };

  const renderPaymentConfirmation = () => {
    const rows: Array<[string, string, string?]> = [
      ['Paying for', title],
      ['From', sourceName],
      ['Amount', `-${money(amount)}`, '#DC2626'],
      [`${sourceName} now`, money(sourceAmount)],
      [`${sourceName} after`, money(sourceAmount - amount), '#059669'],
    ];
    return (
      <Box>
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#F8FAFC' }}>
          {rows.map(([label, value, color], i) => (
            <Stack
              key={label}
              direction="row"
              justifyContent="space-between"
              sx={{
                py: 0.75,
                borderTop: i === 3 ? '1px solid' : 'none',
                borderColor: 'divider',
                mt: i === 3 ? 0.75 : 0,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Box>
        {footer(
          <Button
            variant="contained"
            onClick={handleConfirmPayment}
            disabled={isProcessing}
            startIcon={
              isProcessing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Icon icon="solar:check-circle-bold" width={18} />
              )
            }
            sx={primarySx}
          >
            {isProcessing ? 'Paying...' : `Pay ${money(amount)}`}
          </Button>
        )}
      </Box>
    );
  };

  const renderDeferralOptions = () => {
    const options: Array<[string, string, string]> = [
      ['1', '1 day', 'Pay tomorrow'],
      ['3', '3 days', 'A few more days'],
      ['7', '1 week', 'Pay next week'],
      ['custom', 'Pick a date', 'Up to 2 weeks'],
    ];
    return (
      <Box>
        <Alert
          severity="info"
          icon={<Icon icon="solar:info-circle-bold" width={20} />}
          sx={{ mb: 2, borderRadius: 1.5 }}
        >
          A late fee may be added if you miss the new date. You have {deferralsLeft} of {MAX_DEFERRALS} requests left.
        </Alert>
        <Grid container spacing={1.25}>
          {options.map(([value, label, caption]) => (
            <Grid item xs={6} key={value}>
              <Tile
                icon={value === 'custom' ? 'solar:calendar-search-bold-duotone' : 'solar:clock-circle-bold-duotone'}
                tone="#D97706"
                label={label}
                caption={caption}
                selected={deferralPeriod === value}
                onClick={() => {
                  setValidationError('');
                  setDeferralPeriod(value);
                }}
              />
            </Grid>
          ))}
        </Grid>
        {deferralPeriod === 'custom' && (
          <TextField
            fullWidth
            type="date"
            label="Due date"
            value={customDueDate}
            onChange={(e) => setCustomDueDate(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] }}
          />
        )}
        {errorNote()}
        {footer(
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmDeferral}
            disabled={isProcessing || (deferralPeriod === 'custom' && !customDueDate)}
            startIcon={
              isProcessing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Icon icon="solar:calendar-add-bold" width={18} />
              )
            }
            sx={primarySx}
          >
            {isProcessing ? 'Saving...' : 'Get more time'}
          </Button>
        )}
      </Box>
    );
  };

  const renderQuestionForm = () => (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="What is wrong with this charge?"
        helperText="Your teacher will read this and get back to you."
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        placeholder="For example: I turned in my homework on time."
      />
      {errorNote()}
      {footer(
        <Button
          variant="contained"
          onClick={handleQuestionSubmit}
          disabled={!questionText.trim() || isProcessing}
          startIcon={
            isProcessing ? <CircularProgress size={18} color="inherit" /> : <Icon icon="solar:plain-bold" width={18} />
          }
          sx={primarySx}
        >
          {isProcessing ? 'Sending...' : 'Send question'}
        </Button>
      )}
    </Box>
  );

  const renderContent = () => {
    switch (currentStep) {
      case 'payment-source':
        return renderPaymentSourceSelection();
      case 'confirmation':
        return renderPaymentConfirmation();
      case 'deferral-options':
        return renderDeferralOptions();
      case 'question-form':
        return renderQuestionForm();
      default:
        return renderActionSelector();
    }
  };

  const tone = isCredit ? '#059669' : '#DC2626';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      aria-labelledby="charge-dialog-title"
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}
    >
      {/* Charge header: what it is, how much, and what it does to your balance */}
      <Box sx={{ p: 2.5, pb: 2, bgcolor: alpha(tone, 0.05), borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: alpha(tone, 0.12),
              color: tone,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon={chargeIcon(transaction)} width={28} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="charge-dialog-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {note && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                {note}
              </Typography>
            )}
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.75, letterSpacing: '-0.02em' }}>
              {isCredit ? '+' : ''}
              {money(amount)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Icon icon="solar:close-circle-linear" width={22} />
          </IconButton>
        </Stack>
        {!isCredit && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Your balance
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {money(currentBalance)} → {money(currentBalance - amount)}
            </Typography>
          </Stack>
        )}
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          {STEP_TITLES[currentStep]}
        </Typography>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
